import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Copy } from "lucide-react";
import { API, STATUS_COLORS, formatPrice, resolveImage } from "../../data/site";
import { useAuth } from "../../auth/AuthContext";
import { inputCls, DropZone, StatusPill, AdminButton } from "../../admin/ui";
import DashboardPanel from "../../admin/panels/DashboardPanel";
import CollectionPanel from "../../admin/panels/CollectionPanel";
import TestimonialsPanel from "../../admin/panels/TestimonialsPanel";
import MediaPanel from "../../admin/panels/MediaPanel";
import ContentPanel from "../../admin/panels/ContentPanel";
import AreasPanel from "../../admin/panels/AreasPanel";
import SeoPanel from "../../admin/panels/SeoPanel";
import SettingsPanel from "../../admin/panels/SettingsPanel";
import NavPanel from "../../admin/panels/NavPanel";

const EMPTY_HOME = {
  title: "", status: "Available", price: "", address: "", city: "", state: "ID", zip: "",
  beds: "", baths: "", sqft: "", garage: "", lot: "",
  description: "", details: "", featuresText: "", imagesText: "", floorPlansText: "", listing_url: "",
  featured: false, published: true, archived: false, order: 0,
};

const toPayload = (f) => ({
  title: f.title,
  status: f.status,
  price: f.price === "" ? null : Number(f.price),
  address: f.address,
  city: f.city,
  state: f.state,
  zip: f.zip,
  beds: f.beds === "" ? null : Number(f.beds),
  baths: f.baths === "" ? null : Number(f.baths),
  sqft: f.sqft === "" ? null : Number(f.sqft),
  garage: f.garage,
  lot: f.lot,
  description: f.description,
  details: f.details,
  features: f.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
  images: f.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
  floor_plans: f.floorPlansText.split("\n").map((s) => s.trim()).filter(Boolean),
  featured: f.featured,
  listing_url: f.listing_url,
  published: !!f.published,
  archived: !!f.archived,
  order: Number(f.order) || 0,
});

const fromHome = (h) => ({
  title: h.title, status: h.status, price: h.price ?? "", address: h.address, city: h.city,
  state: h.state || "ID", zip: h.zip || "",
  beds: h.beds ?? "", baths: h.baths ?? "", sqft: h.sqft ?? "", garage: h.garage || "", lot: h.lot || "",
  description: h.description || "", details: h.details || "",
  featuresText: (h.features || []).join("\n"),
  imagesText: (h.images || []).join("\n"), floorPlansText: (h.floor_plans || []).join("\n"),
  listing_url: h.listing_url || "",
  featured: !!h.featured, published: h.published !== false, archived: !!h.archived, order: h.order || 0,
});

const HomeForm = ({ initial, onSave, onCancel, saving }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const addImage = (url) => setF((prev) => ({ ...prev, imagesText: prev.imagesText ? `${prev.imagesText}\n${url}` : url }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(toPayload(f)); }} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="home-form">
      <label className="block md:col-span-2"><span className="eyebrow text-[#5C5A56] block mb-1">Title *</span>
        <input required value={f.title} onChange={set("title")} className={inputCls} data-testid="home-form-title" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Status</span>
        <select value={f.status} onChange={set("status")} className={inputCls} data-testid="home-form-status">
          {Object.keys(STATUS_COLORS).map((s) => <option key={s}>{s}</option>)}
        </select></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Price</span>
        <input type="number" value={f.price} onChange={set("price")} className={inputCls} data-testid="home-form-price" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Address</span>
        <input value={f.address} onChange={set("address")} className={inputCls} data-testid="home-form-address" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">City</span>
        <input value={f.city} onChange={set("city")} className={inputCls} data-testid="home-form-city" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">State</span>
        <input value={f.state} onChange={set("state")} className={inputCls} data-testid="home-form-state" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">ZIP</span>
        <input value={f.zip} onChange={set("zip")} className={inputCls} data-testid="home-form-zip" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Display Order</span>
        <input type="number" value={f.order} onChange={set("order")} className={inputCls} data-testid="home-form-order" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Beds</span>
        <input type="number" step="0.5" value={f.beds} onChange={set("beds")} className={inputCls} data-testid="home-form-beds" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Baths</span>
        <input type="number" step="0.5" value={f.baths} onChange={set("baths")} className={inputCls} data-testid="home-form-baths" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Sq Ft</span>
        <input type="number" value={f.sqft} onChange={set("sqft")} className={inputCls} data-testid="home-form-sqft" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Garage</span>
        <input value={f.garage} onChange={set("garage")} className={inputCls} placeholder="3-Car Garage" data-testid="home-form-garage" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Lot</span>
        <input value={f.lot} onChange={set("lot")} className={inputCls} placeholder="0.25 Acre Lot" data-testid="home-form-lot" /></label>
      <label className="flex items-end gap-2 pb-2">
        <input type="checkbox" checked={f.featured} onChange={set("featured")} className="w-4 h-4 accent-[#8C6A4F]" data-testid="home-form-featured" />
        <span className="eyebrow text-[#5C5A56]">Featured on Homepage</span></label>
      <label className="flex items-end gap-2 pb-2">
        <input type="checkbox" checked={f.published} onChange={set("published")} className="w-4 h-4 accent-[#8C6A4F]" data-testid="home-form-published" />
        <span className="eyebrow text-[#5C5A56]">Published (live)</span></label>
      <label className="flex items-end gap-2 pb-2">
        <input type="checkbox" checked={f.archived} onChange={set("archived")} className="w-4 h-4 accent-[#8C6A4F]" data-testid="home-form-archived" />
        <span className="eyebrow text-[#5C5A56]">Archived</span></label>
      <label className="block md:col-span-3"><span className="eyebrow text-[#5C5A56] block mb-1">Description</span>
        <textarea rows={4} value={f.description} onChange={set("description")} className={inputCls} data-testid="home-form-description" /></label>
      <label className="block md:col-span-3"><span className="eyebrow text-[#5C5A56] block mb-1">Additional Details</span>
        <textarea rows={2} value={f.details} onChange={set("details")} className={inputCls} data-testid="home-form-details" /></label>
      <label className="block md:col-span-3"><span className="eyebrow text-[#5C5A56] block mb-1">Features (one per line)</span>
        <textarea rows={3} value={f.featuresText} onChange={set("featuresText")} className={inputCls} data-testid="home-form-features" /></label>
      <label className="block md:col-span-3"><span className="eyebrow text-[#5C5A56] block mb-1">Listing URL (Zillow, Redfin, etc.)</span>
        <input value={f.listing_url} onChange={set("listing_url")} className={inputCls} placeholder="https://www.zillow.com/homedetails/..." data-testid="home-form-listing-url" /></label>
      <div className="md:col-span-3">
        <span className="eyebrow text-[#5C5A56] block mb-1">Photos (one URL per line — first photo is the main photo)</span>
        <textarea rows={3} value={f.imagesText} onChange={set("imagesText")} className={inputCls} data-testid="home-form-images" />
        <DropZone onUploaded={addImage} accept="image/*" category="Homes for Sale" label="Drag & drop photos here, or click to browse" testid="home-form-dropzone" />
      </div>
      <label className="block md:col-span-3"><span className="eyebrow text-[#5C5A56] block mb-1">Floor Plan Image URLs (one per line)</span>
        <textarea rows={2} value={f.floorPlansText} onChange={set("floorPlansText")} className={inputCls} data-testid="home-form-floorplans" /></label>
      <div className="md:col-span-3 flex gap-3 pt-2">
        <AdminButton primary disabled={saving} testid="home-form-save-btn">{saving ? "Saving…" : "Save Home"}</AdminButton>
        <AdminButton onClick={onCancel} testid="home-form-cancel-btn">Cancel</AdminButton>
      </div>
    </form>
  );
};

const EMPTY_PLAN = { name: "", sqft: "", beds: "", baths: "", garage: "", stories: "", width: "", depth: "", description: "", featuresText: "", imagesText: "", featured: false, published: true, archived: false, order: 0 };

const planPayload = (f) => ({
  name: f.name,
  sqft: f.sqft === "" ? null : Number(f.sqft),
  beds: f.beds === "" ? null : Number(f.beds),
  baths: f.baths === "" ? null : Number(f.baths),
  garage: f.garage,
  stories: f.stories,
  width: f.width,
  depth: f.depth,
  description: f.description,
  features: f.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
  images: f.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
  featured: !!f.featured,
  published: !!f.published,
  archived: !!f.archived,
  order: Number(f.order) || 0,
});

const PlanForm = ({ initial, onSave, onCancel, saving }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const addFile = (url) => setF((prev) => ({ ...prev, imagesText: prev.imagesText ? `${prev.imagesText}\n${url}` : url }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(planPayload(f)); }} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plan-form">
      <label className="block md:col-span-2"><span className="eyebrow text-[#5C5A56] block mb-1">Plan Name *</span>
        <input required value={f.name} onChange={set("name")} className={inputCls} placeholder="The Owyhee" data-testid="plan-form-name" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Approx. Sq Ft</span>
        <input type="number" value={f.sqft} onChange={set("sqft")} className={inputCls} data-testid="plan-form-sqft" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Beds</span>
        <input type="number" step="0.5" value={f.beds} onChange={set("beds")} className={inputCls} data-testid="plan-form-beds" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Baths</span>
        <input type="number" step="0.5" value={f.baths} onChange={set("baths")} className={inputCls} data-testid="plan-form-baths" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Garage</span>
        <input value={f.garage} onChange={set("garage")} className={inputCls} placeholder="3-Car + RV Bay" data-testid="plan-form-garage" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Stories</span>
        <input value={f.stories} onChange={set("stories")} className={inputCls} placeholder="1 or 2" data-testid="plan-form-stories" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Width</span>
        <input value={f.width} onChange={set("width")} className={inputCls} placeholder="62 ft" data-testid="plan-form-width" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Depth</span>
        <input value={f.depth} onChange={set("depth")} className={inputCls} placeholder="74 ft" data-testid="plan-form-depth" /></label>
      <label className="block"><span className="eyebrow text-[#5C5A56] block mb-1">Display Order</span>
        <input type="number" value={f.order} onChange={set("order")} className={inputCls} data-testid="plan-form-order" /></label>
      <label className="flex items-end gap-2 pb-2">
        <input type="checkbox" checked={f.featured} onChange={set("featured")} className="w-4 h-4 accent-[#8C6A4F]" data-testid="plan-form-featured" />
        <span className="eyebrow text-[#5C5A56]">Featured</span></label>
      <label className="flex items-end gap-2 pb-2">
        <input type="checkbox" checked={f.published} onChange={set("published")} className="w-4 h-4 accent-[#8C6A4F]" data-testid="plan-form-published" />
        <span className="eyebrow text-[#5C5A56]">Published (live)</span></label>
      <label className="flex items-end gap-2 pb-2">
        <input type="checkbox" checked={f.archived} onChange={set("archived")} className="w-4 h-4 accent-[#8C6A4F]" data-testid="plan-form-archived" />
        <span className="eyebrow text-[#5C5A56]">Archived</span></label>
      <label className="block md:col-span-3"><span className="eyebrow text-[#5C5A56] block mb-1">Short Description (optional)</span>
        <textarea rows={2} value={f.description} onChange={set("description")} className={inputCls} data-testid="plan-form-description" /></label>
      <label className="block md:col-span-3"><span className="eyebrow text-[#5C5A56] block mb-1">Features (one per line, optional)</span>
        <textarea rows={2} value={f.featuresText} onChange={set("featuresText")} className={inputCls} data-testid="plan-form-features" /></label>
      <div className="md:col-span-3">
        <span className="eyebrow text-[#5C5A56] block mb-1">Plan Sheets — Images or PDFs (one URL per line — first is the main sheet)</span>
        <textarea rows={3} value={f.imagesText} onChange={set("imagesText")} className={inputCls} placeholder="Paste URLs or drop files below — each upload adds a line" data-testid="plan-form-images" />
        <DropZone onUploaded={addFile} accept="image/*,.pdf" category="Floor Plans" label="Drag & drop images or PDFs here, or click to browse" testid="plan-form-dropzone" />
      </div>
      <div className="md:col-span-3 flex gap-3 pt-2">
        <AdminButton primary disabled={saving} testid="plan-form-save-btn">{saving ? "Saving…" : "Save Plan"}</AdminButton>
        <AdminButton onClick={onCancel} testid="plan-form-cancel-btn">Cancel</AdminButton>
      </div>
    </form>
  );
};

const fromPlan = (p) => ({
  name: p.name, sqft: p.sqft ?? "", beds: p.beds ?? "", baths: p.baths ?? "",
  garage: p.garage || "", stories: p.stories || "", width: p.width || "", depth: p.depth || "",
  description: p.description || "", featuresText: (p.features || []).join("\n"),
  imagesText: (p.images?.length ? p.images : p.image ? [p.image] : []).join("\n"),
  featured: !!p.featured, published: p.published !== false, archived: !!p.archived, order: p.order || 0,
});

const SitePhotosPanel = ({ photos, onAdd, onDelete }) => {
  const [url, setUrl] = useState("");
  return (
    <div data-testid="admin-photos-panel">
      <p className="text-sm text-[#5C5A56] max-w-2xl mb-8">
        These photos join the automatic rotation across the website — the homepage collage, portfolio,
        Instagram grid, and city pages all cycle through them.
      </p>
      <div className="flex flex-wrap gap-3 mb-4">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste an image URL…" data-testid="pool-url-input"
          className={`${inputCls} flex-1 min-w-[240px]`} />
        <AdminButton onClick={() => { if (url.trim()) { onAdd(url.trim()); setUrl(""); } }} testid="pool-add-url-btn">Add URL</AdminButton>
      </div>
      <DropZone onUploaded={onAdd} accept="image/*" category="Homepage" label="Drag & drop photos here, or click to browse — multiple at once" testid="pool-dropzone" />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-10" data-testid="pool-grid">
        {photos.map((p) => (
          <div key={p.id} className="relative group aspect-[4/3] bg-[#EFECE7] overflow-hidden" data-testid={`pool-photo-${p.id}`}>
            <img src={resolveImage(p.url)} alt="Site rotation photo" className="w-full h-full object-cover" loading="lazy" />
            <button onClick={() => onDelete(p.id)} data-testid={`pool-delete-${p.id}`} aria-label="Remove photo"
              className="absolute top-2 right-2 w-7 h-7 bg-[#1A1A1A]/85 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-red-600">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      {photos.length === 0 && <p className="py-10 text-[#A3A09B] font-mono text-xs tracking-[0.2em] uppercase">No extra photos yet — upload your first one above.</p>}
    </div>
  );
};

const LEAD_STATUSES = ["New", "Contacted", "Follow-up", "Closed"];
const LEAD_COLORS = { New: "bg-[#A37C45] text-white", Contacted: "bg-[#4E735B] text-white", "Follow-up": "bg-[#6B7280] text-white", Closed: "bg-[#4B4B4B] text-white" };

const LeadsPanel = ({ leads, onStatus, onDelete, onNotes }) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [openNotes, setOpenNotes] = useState(null);
  const [noteText, setNoteText] = useState("");

  const filtered = leads.filter((l) => {
    if (filter !== "All" && l.status !== filter) return false;
    if (search) {
      const hay = `${l.name} ${l.email} ${l.phone} ${l.interest} ${l.message} ${l.location}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div data-testid="admin-leads-panel">
      <div className="flex flex-wrap gap-3 mb-6">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search inquiries…" data-testid="leads-search"
          className={`${inputCls} flex-1 min-w-[220px]`} />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${inputCls} w-auto`} data-testid="leads-filter">
          {["All", ...LEAD_STATUSES].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="text-left border-b border-[rgba(26,26,26,0.15)]">
              {["Name", "Contact", "Interest", "Project Details", "Received", "Status", ""].map((h) => (
                <th key={h} className="eyebrow text-[#5C5A56] py-3 pr-6 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="border-b border-[rgba(26,26,26,0.08)] align-top" data-testid={`lead-row-${l.id}`}>
                <td className="py-4 pr-6 font-serif text-lg whitespace-nowrap">{l.name}</td>
                <td className="py-4 pr-6 text-[#5C5A56] text-xs">
                  <a href={`mailto:${l.email}`} className="hover:text-[#8C6A4F]">{l.email}</a>
                  {l.phone && <div>{l.phone}</div>}
                </td>
                <td className="py-4 pr-6 text-xs">{l.interest}{l.home ? <div className="text-[#8C6A4F] mt-1">{l.home}</div> : null}</td>
                <td className="py-4 pr-6 text-xs text-[#5C5A56] max-w-xs">
                  {l.location && <div>Where: {l.location}</div>}
                  {l.home_size && <div>Size: {l.home_size}</div>}
                  {l.budget && <div>Budget: {l.budget}</div>}
                  {l.timeframe && <div>Timeline: {l.timeframe}</div>}
                  {l.message && <div className="mt-1 italic">"{l.message}"</div>}
                  {l.notes && <div className="mt-1 text-[#8C6A4F]">Note: {l.notes}</div>}
                </td>
                <td className="py-4 pr-6 text-xs text-[#A3A09B] whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="py-4 pr-6">
                  <span className={`inline-block font-mono text-[0.58rem] tracking-[0.15em] uppercase font-bold px-2.5 py-1 mb-2 ${LEAD_COLORS[l.status] || LEAD_COLORS.New}`}>{l.status}</span>
                  <select value={l.status} onChange={(e) => onStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`}
                    className="block border border-[#1A1A1A]/15 bg-white px-2 py-1.5 text-xs font-mono uppercase tracking-wider">
                    {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="py-4 text-right whitespace-nowrap">
                  <button onClick={() => { setOpenNotes(openNotes === l.id ? null : l.id); setNoteText(l.notes || ""); }} className="p-2 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#8C6A4F] hover:underline" data-testid={`lead-notes-${l.id}`}>
                    Notes
                  </button>
                  <button onClick={() => onDelete(l.id)} className="p-2 hover:text-red-600 transition-colors" data-testid={`delete-lead-${l.id}`} aria-label="Delete"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="py-10 text-[#A3A09B] font-mono text-xs tracking-[0.2em] uppercase">No inquiries match.</p>}
      </div>
      {openNotes && (
        <div className="mt-4 max-w-lg bg-white border border-[rgba(26,26,26,0.15)] p-5" data-testid="lead-notes-editor">
          <p className="eyebrow text-[#8C6A4F] mb-3">Internal Notes — {leads.find((l) => l.id === openNotes)?.name}</p>
          <textarea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} className={inputCls} placeholder="Private notes — never shown publicly" data-testid="lead-notes-input" />
          <div className="flex gap-3 mt-3">
            <AdminButton primary type="button" onClick={async () => { await onNotes(openNotes, noteText); setOpenNotes(null); }} testid="lead-notes-save">Save Note</AdminButton>
            <AdminButton onClick={() => setOpenNotes(null)}>Cancel</AdminButton>
          </div>
        </div>
      )}
    </div>
  );
};

const PROJECT_FIELDS = [
  { k: "name", label: "Project Name", required: true, span: 2 },
  { k: "location", label: "Location" },
  { k: "sqft", label: "Sq Ft", type: "number" },
  { k: "beds", label: "Bedrooms", type: "number" },
  { k: "baths", label: "Bathrooms", type: "number" },
  { k: "year", label: "Year Completed" },
  { k: "floor_plan", label: "Floor Plan Name (optional)" },
  { k: "order", label: "Display Order", type: "number", default: 0 },
  { k: "description", label: "Description", type: "textarea", rows: 3 },
  { k: "features", label: "Special Features", type: "lines", rows: 3 },
  { k: "images", label: "Photos (first is the featured image)", type: "images", category: "Projects" },
  { k: "featured", label: "Featured Project", type: "toggle" },
  { k: "published", label: "Published (live)", type: "toggle", default: true },
  { k: "archived", label: "Archived", type: "toggle" },
];

const SECTIONS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "homes", label: "Homes" },
  { id: "plans", label: "Floor Plans" },
  { id: "projects", label: "Projects" },
  { id: "testimonials", label: "Testimonials" },
  { id: "photos", label: "Site Photos" },
  { id: "media", label: "Media Library" },
  { id: "leads", label: "Inquiries" },
  { id: "content", label: "Website Text" },
  { id: "areas", label: "Service Areas" },
  { id: "seo", label: "SEO" },
  { id: "nav", label: "Navigation" },
  { id: "settings", label: "Settings" },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("dashboard");
  const [newNonce, setNewNonce] = useState(0);
  const [newFor, setNewFor] = useState("");
  const [homes, setHomes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [plans, setPlans] = useState([]);
  const [poolPhotos, setPoolPhotos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [planEditing, setPlanEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const { data: adminTestimonials = [] } = useQuery({
    queryKey: ["testimonials-admin"],
    queryFn: async () => (await axios.get(`${API}/testimonials-admin`, { withCredentials: true })).data,
    enabled: !!user,
  });
  const pendingTestimonials = adminTestimonials.filter((t) => t.status === "pending").length;

  const load = async () => {
    const [h, l, p, pp] = await Promise.all([
      axios.get(`${API}/homes-admin`, { withCredentials: true }),
      axios.get(`${API}/leads`, { withCredentials: true }),
      axios.get(`${API}/floor-plans-admin`, { withCredentials: true }),
      axios.get(`${API}/pool-photos`, { withCredentials: true }),
    ]);
    setHomes(h.data);
    setLeads(l.data);
    setPlans(p.data);
    setPoolPhotos(pp.data);
  };

  useEffect(() => {
    if (user === false) navigate("/admin/login");
    if (user) load();
  }, [user]);

  if (!user) return null;

  const go = (section, action) => {
    setTab(section);
    if (action === "new") {
      setNewNonce(Date.now());
      setNewFor(section);
      if (section === "homes") setEditing("new");
      if (section === "plans") setPlanEditing("new");
    }
  };

  const saveHome = async (payload) => {
    setSaving(true);
    try {
      if (editing === "new") await axios.post(`${API}/homes`, payload, { withCredentials: true });
      else await axios.put(`${API}/homes/${editing.id}`, payload, { withCredentials: true });
      toast.success(payload.published ? "Home saved — live on the website" : "Home saved as draft");
      setEditing(null);
      await load();
      queryClient.invalidateQueries({ queryKey: ["homes"] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const homeAction = async (h, changes, msg) => {
    if (msg && !window.confirm(msg)) return;
    const payload = { ...h, ...changes };
    delete payload.id;
    delete payload.created_at;
    await axios.put(`${API}/homes/${h.id}`, payload, { withCredentials: true });
    toast.success("Updated");
    load();
    queryClient.invalidateQueries({ queryKey: ["homes"] });
  };

  const duplicateHome = async (h) => {
    const payload = { ...h, title: `${h.title} (Copy)`, published: false };
    delete payload.id;
    delete payload.created_at;
    await axios.post(`${API}/homes`, payload, { withCredentials: true });
    toast.success("Home duplicated as a draft");
    load();
  };

  const deleteHome = async (h) => {
    if (!window.confirm(`Permanently delete "${h.title}"? Consider archiving or marking it Sold instead — sold homes stay in your history.`)) return;
    await axios.delete(`${API}/homes/${h.id}`, { withCredentials: true });
    toast.success("Home deleted");
    load();
  };

  const savePlan = async (payload) => {
    setSaving(true);
    try {
      if (planEditing === "new") await axios.post(`${API}/floor-plans`, payload, { withCredentials: true });
      else await axios.put(`${API}/floor-plans/${planEditing.id}`, payload, { withCredentials: true });
      toast.success(payload.published ? "Floor plan saved — live" : "Floor plan saved as draft");
      setPlanEditing(null);
      await load();
      queryClient.invalidateQueries({ queryKey: ["floor-plans"] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const planAction = async (p, changes, msg) => {
    if (msg && !window.confirm(msg)) return;
    const payload = { ...p, ...changes };
    delete payload.id;
    delete payload.created_at;
    await axios.put(`${API}/floor-plans/${p.id}`, payload, { withCredentials: true });
    toast.success("Updated");
    load();
    queryClient.invalidateQueries({ queryKey: ["floor-plans"] });
  };

  const duplicatePlan = async (p) => {
    const payload = { ...p, name: `${p.name} (Copy)`, published: false };
    delete payload.id;
    delete payload.created_at;
    await axios.post(`${API}/floor-plans`, payload, { withCredentials: true });
    toast.success("Plan duplicated as a draft");
    load();
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Permanently delete this floor plan?")) return;
    await axios.delete(`${API}/floor-plans/${id}`, { withCredentials: true });
    toast.success("Floor plan deleted");
    load();
  };

  const setLeadStatus = async (id, status) => {
    await axios.patch(`${API}/leads/${id}`, { status }, { withCredentials: true });
    load();
    queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const setLeadNotes = async (id, notes) => {
    await axios.patch(`${API}/leads/${id}`, { notes }, { withCredentials: true });
    toast.success("Note saved");
    load();
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await axios.delete(`${API}/leads/${id}`, { withCredentials: true });
    load();
  };

  const addPoolPhoto = async (url) => {
    if (!url) return;
    await axios.post(`${API}/pool-photos`, { url }, { withCredentials: true });
    toast.success("Photo added to rotation");
    load();
    queryClient.invalidateQueries({ queryKey: ["pool-photos"] });
  };

  const deletePoolPhoto = async (id) => {
    if (!window.confirm("Remove this photo from the rotation?")) return;
    await axios.delete(`${API}/pool-photos/${id}`, { withCredentials: true });
    toast.success("Photo removed");
    load();
    queryClient.invalidateQueries({ queryKey: ["pool-photos"] });
  };

  const visibleHomes = showArchived ? homes : homes.filter((h) => !h.archived);
  const visiblePlans = showArchived ? plans : plans.filter((p) => !p.archived);

  return (
    <main className="min-h-screen bg-[#F7F5F2] pt-20 md:pt-24 pb-20" data-testid="admin-dashboard">
      <div className="max-w-[1500px] mx-auto px-4 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-[rgba(26,26,26,0.12)]">
          <div>
            <p className="eyebrow text-[#8C6A4F]">On Point Homes</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight mt-1 capitalize">{SECTIONS.find((s) => s.id === tab)?.label}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-mono text-[0.62rem] tracking-[0.15em] uppercase font-bold border border-[#1A1A1A]/25 px-5 py-2.5 hover:bg-[#1A1A1A] hover:text-white transition-colors duration-300" data-testid="admin-view-site-btn">
              View Site
            </Link>
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 font-mono text-[0.62rem] tracking-[0.15em] uppercase font-bold border border-[#1A1A1A]/25 px-5 py-2.5 hover:bg-[#1A1A1A] hover:text-white transition-colors duration-300">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 mt-6">
          <nav className="lg:w-52 shrink-0 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" aria-label="Admin sections" data-testid="admin-nav">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setTab(s.id)}
                data-testid={`tab-${s.id}`}
                className={`text-left whitespace-nowrap font-mono text-[0.68rem] tracking-[0.18em] uppercase font-bold px-4 py-3 transition-colors duration-300 border-l-2 ${
                  tab === s.id ? "border-[#8C6A4F] text-[#1A1A1A] bg-white/60" : "border-transparent text-[#A3A09B] hover:text-[#1A1A1A]"
                }`}
              >
                {s.label}
                {s.id === "leads" && leads.filter((l) => l.status === "New").length > 0 && (
                  <span className="ml-2 bg-[#8C6A4F] text-white text-[0.55rem] px-1.5 py-0.5">{leads.filter((l) => l.status === "New").length}</span>
                )}
                {s.id === "testimonials" && pendingTestimonials > 0 && (
                  <span className="ml-2 bg-[#A37C45] text-white text-[0.55rem] px-1.5 py-0.5" data-testid="testimonials-pending-badge">{pendingTestimonials}</span>
                )}
              </button>
            ))}
          </nav>

          <div className="flex-1 min-w-0">
            {tab === "dashboard" && <DashboardPanel go={go} />}

            {tab === "homes" && (
              <div data-testid="admin-homes-panel">
                {editing === null && (
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <AdminButton primary onClick={() => setEditing("new")} testid="add-home-btn"><span className="inline-flex items-center gap-2"><Plus size={14} /> Add Home</span></AdminButton>
                    <label className="flex items-center gap-2 font-mono text-[0.62rem] tracking-[0.15em] uppercase font-bold text-[#5C5A56] cursor-pointer">
                      <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4 accent-[#8C6A4F]" data-testid="homes-show-archived" />
                      Show Archived
                    </label>
                  </div>
                )}
                {editing !== null && (
                  <div className="mb-10 border border-[rgba(26,26,26,0.15)] p-6 md:p-8 bg-white/50">
                    <h2 className="font-serif text-2xl mb-6">{editing === "new" ? "Add a Home" : `Edit — ${editing.title}`}</h2>
                    <HomeForm initial={editing === "new" ? EMPTY_HOME : fromHome(editing)} onSave={saveHome} onCancel={() => setEditing(null)} saving={saving} />
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="homes-table">
                    <thead>
                      <tr className="text-left border-b border-[rgba(26,26,26,0.15)]">
                        {["Home", "Status", "Price", "City", "Visibility", ""].map((h) => (
                          <th key={h} className="eyebrow text-[#5C5A56] py-3 pr-6 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleHomes.map((h) => (
                        <tr key={h.id} className="border-b border-[rgba(26,26,26,0.08)]" data-testid={`home-row-${h.id}`}>
                          <td className="py-4 pr-6 font-serif text-lg">{h.title}{h.featured && <span className="ml-2 font-mono text-[0.55rem] tracking-[0.15em] uppercase text-[#8C6A4F]">Featured</span>}</td>
                          <td className="py-4 pr-6">
                            <select value={h.status} onChange={(e) => homeAction(h, { status: e.target.value }, e.target.value === "Sold" ? `Mark "${h.title}" as Sold? It will leave the active listings and move to the Sold tab.` : null)} data-testid={`home-status-${h.id}`}
                              className="border border-[#1A1A1A]/15 bg-white px-2 py-1.5 text-xs font-mono uppercase tracking-wider">
                              {Object.keys(STATUS_COLORS).map((s) => <option key={s}>{s}</option>)}
                            </select>
                          </td>
                          <td className="py-4 pr-6 font-mono text-xs">{formatPrice(h.price)}</td>
                          <td className="py-4 pr-6 text-[#5C5A56]">{h.city}</td>
                          <td className="py-4 pr-6"><StatusPill published={h.published !== false} archived={h.archived} testid={`home-pub-${h.id}`} /></td>
                          <td className="py-4 text-right whitespace-nowrap">
                            {h.published === false || h.archived ? (
                              <button onClick={() => homeAction(h, { published: true, archived: false }, `Publish "${h.title}" to the live website?`)} className="p-2 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#4E735B] hover:underline" data-testid={`home-publish-${h.id}`}>Publish</button>
                            ) : (
                              <button onClick={() => homeAction(h, { published: false }, `Unpublish "${h.title}"? It will be hidden from the website but kept here.`)} className="p-2 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#A37C45] hover:underline" data-testid={`home-unpublish-${h.id}`}>Unpublish</button>
                            )}
                            <button onClick={() => duplicateHome(h)} className="p-2 hover:text-[#8C6A4F] transition-colors" data-testid={`duplicate-home-${h.id}`} aria-label="Duplicate"><Copy size={15} /></button>
                            <button onClick={() => setEditing(h)} className="p-2 hover:text-[#8C6A4F] transition-colors" data-testid={`edit-home-${h.id}`} aria-label="Edit"><Pencil size={15} /></button>
                            <button onClick={() => deleteHome(h)} className="p-2 hover:text-red-600 transition-colors" data-testid={`delete-home-${h.id}`} aria-label="Delete"><Trash2 size={15} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {visibleHomes.length === 0 && <p className="py-10 text-[#A3A09B] font-mono text-xs tracking-[0.2em] uppercase">No homes — add your first one.</p>}
                </div>
              </div>
            )}

            {tab === "plans" && (
              <div data-testid="admin-plans-panel">
                {planEditing === null && (
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <AdminButton primary onClick={() => setPlanEditing("new")} testid="add-plan-btn"><span className="inline-flex items-center gap-2"><Plus size={14} /> Add Floor Plan</span></AdminButton>
                    <label className="flex items-center gap-2 font-mono text-[0.62rem] tracking-[0.15em] uppercase font-bold text-[#5C5A56] cursor-pointer">
                      <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4 accent-[#8C6A4F]" />
                      Show Archived
                    </label>
                  </div>
                )}
                {planEditing !== null && (
                  <div className="mb-10 border border-[rgba(26,26,26,0.15)] p-6 md:p-8 bg-white/50">
                    <h2 className="font-serif text-2xl mb-6">{planEditing === "new" ? "Add a Floor Plan" : `Edit — ${planEditing.name}`}</h2>
                    <PlanForm initial={planEditing === "new" ? EMPTY_PLAN : fromPlan(planEditing)} onSave={savePlan} onCancel={() => setPlanEditing(null)} saving={saving} />
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="plans-table">
                    <thead>
                      <tr className="text-left border-b border-[rgba(26,26,26,0.15)]">
                        {["Plan", "Sq Ft", "Bed / Bath", "Garage", "Visibility", ""].map((h) => (
                          <th key={h} className="eyebrow text-[#5C5A56] py-3 pr-6 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visiblePlans.map((p) => (
                        <tr key={p.id} className="border-b border-[rgba(26,26,26,0.08)]" data-testid={`plan-row-${p.id}`}>
                          <td className="py-4 pr-6 font-serif text-lg">{p.name}</td>
                          <td className="py-4 pr-6 font-mono text-xs">{p.sqft ? p.sqft.toLocaleString() : "—"}</td>
                          <td className="py-4 pr-6 text-[#5C5A56]">{p.beds ?? "—"} bd / {p.baths ?? "—"} ba</td>
                          <td className="py-4 pr-6 text-[#5C5A56]">{p.garage || "—"}</td>
                          <td className="py-4 pr-6"><StatusPill published={p.published !== false} archived={p.archived} testid={`plan-pub-${p.id}`} /></td>
                          <td className="py-4 text-right whitespace-nowrap">
                            {p.published === false || p.archived ? (
                              <button onClick={() => planAction(p, { published: true, archived: false }, `Publish "${p.name}" to the live website?`)} className="p-2 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#4E735B] hover:underline" data-testid={`plan-publish-${p.id}`}>Publish</button>
                            ) : (
                              <button onClick={() => planAction(p, { published: false }, `Unpublish "${p.name}"? Hidden from the website but kept here.`)} className="p-2 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#A37C45] hover:underline" data-testid={`plan-unpublish-${p.id}`}>Unpublish</button>
                            )}
                            <button onClick={() => duplicatePlan(p)} className="p-2 hover:text-[#8C6A4F] transition-colors" data-testid={`duplicate-plan-${p.id}`} aria-label="Duplicate"><Copy size={15} /></button>
                            <button onClick={() => setPlanEditing(p)} className="p-2 hover:text-[#8C6A4F] transition-colors" data-testid={`edit-plan-${p.id}`} aria-label="Edit"><Pencil size={15} /></button>
                            <button onClick={() => deletePlan(p.id)} className="p-2 hover:text-red-600 transition-colors" data-testid={`delete-plan-${p.id}`} aria-label="Delete"><Trash2 size={15} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {visiblePlans.length === 0 && <p className="py-10 text-[#A3A09B] font-mono text-xs tracking-[0.2em] uppercase">No floor plans yet — add your first one.</p>}
                </div>
              </div>
            )}

            {tab === "projects" && (
              <CollectionPanel
                title="Projects" singular="Project" apiBase="projects" testid="projects-panel"
                fields={PROJECT_FIELDS}
                columns={[
                  { k: "name", label: "Project", primary: true },
                  { k: "location", label: "Location" },
                  { k: "sqft", label: "Sq Ft", render: (p) => (p.sqft ? p.sqft.toLocaleString() : "—") },
                  { k: "year", label: "Year" },
                ]}
                emptyText="No projects yet — add your first completed home."
                openNewKey={newFor === "projects" ? newNonce : 0}
              />
            )}

            {tab === "testimonials" && (
              <TestimonialsPanel openNewKey={newFor === "testimonials" ? newNonce : 0} />
            )}

            {tab === "photos" && <SitePhotosPanel photos={poolPhotos} onAdd={addPoolPhoto} onDelete={deletePoolPhoto} />}
            {tab === "media" && <MediaPanel />}
            {tab === "leads" && <LeadsPanel leads={leads} onStatus={setLeadStatus} onDelete={deleteLead} onNotes={setLeadNotes} />}
            {tab === "content" && <ContentPanel />}
            {tab === "areas" && <AreasPanel />}
            {tab === "seo" && <SeoPanel />}
            {tab === "nav" && <NavPanel />}
            {tab === "settings" && <SettingsPanel />}
          </div>
        </div>
      </div>
    </main>
  );
};

export default AdminDashboard;
